import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { img } from '../data/content';

const LINKS = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Machines & Products', path: '/products' },
  { label: 'Industries & Process', path: '/industries' },
  { label: 'Contact Us', path: '/contact' }
];

const slug = (path) => (path === '/' ? 'home' : path.slice(1));

function Brand() {
  return (
    <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
      <img src={img('logo.jpg')} alt="Tekwash Systems Pvt Ltd" className="h-11 w-auto mix-blend-screen" />
      <span className="hidden md:flex items-center gap-2 border-l border-steelline pl-3">
        <span className="h-2 w-2 rounded-full bg-green-400 animate-pulseDot" aria-hidden="true" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">Pune Facility Operational</span>
      </span>
    </Link>
  );
}

function DesktopNav({ pathname }) {
  return (
    <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links-desktop">
      {LINKS.map((l) => (
        <Link
          key={l.path}
          to={l.path}
          data-testid={`nav-link-${slug(l.path)}`}
          className={`font-body text-sm tracking-wide transition-colors duration-200 hover:text-paper ${pathname === l.path ? 'text-flame' : 'text-fog'}`}
        >
          {l.label}
        </Link>
      ))}
    </nav>
  );
}

function MenuToggle({ open, onToggle }) {
  return (
    <button
      data-testid="nav-mobile-toggle"
      aria-label="Toggle menu"
      aria-expanded={open}
      onClick={onToggle}
      className="lg:hidden w-11 h-11 flex flex-col items-center justify-center gap-[5px] border border-steelline rounded-sm"
    >
      <span className={`h-[2px] w-5 bg-paper transition-transform duration-300 ${open ? 'rotate-45 translate-y-[7px]' : ''}`} />
      <span className={`h-[2px] w-5 bg-paper transition-opacity ${open ? 'opacity-0' : ''}`} />
      <span className={`h-[2px] w-5 bg-paper transition-transform duration-300 ${open ? '-rotate-45 -translate-y-[7px]' : ''}`} />
    </button>
  );
}

function MobileMenu({ open, pathname, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.nav
          data-testid="nav-links-mobile"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="lg:hidden overflow-hidden border-t border-steelline/60 bg-ink/95 backdrop-blur-xl"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                onClick={onClose}
                data-testid={`nav-mobile-link-${slug(l.path)}`}
                className={`py-3 font-body text-base border-b border-steelline/40 ${pathname === l.path ? 'text-flame' : 'text-paper'}`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/contact" onClick={onClose} className="mt-4 mb-2 bg-flame text-white text-center font-semibold py-3 clip-notch" data-testid="nav-mobile-cta">
              Request Technical Audit
            </Link>
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
}

export default function Navbar() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-ink/80 border-b border-steelline/60">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Brand />
        <DesktopNav pathname={pathname} />
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-request-audit"
            className="hidden sm:inline-flex items-center gap-2 bg-flame text-white font-body text-sm font-semibold px-5 py-2.5 clip-notch hover:bg-flamedim transition-colors duration-200"
          >
            Request Technical Audit
          </Link>
          <MenuToggle open={open} onToggle={() => setOpen((o) => !o)} />
        </div>
      </div>
      <MobileMenu open={open} pathname={pathname} onClose={() => setOpen(false)} />
    </header>
  );
}
